'use client';

import { PortableText } from '@portabletext/react';
import { cn } from '@/lib/utils';
import Image from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';
import CheckList from './CheckList';
import { DarkHalftone } from './textures';

import coin from '@/public/text-only-crypto/bitcoin-coin.png';

/** Figma: desktop 7218:181, mobile 8662:24649. */
export default function WhySection({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  const bodyText =
    'richtext richtext-start w-full prose-p:font-geist prose-p:font-normal prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:tracking-[-0.0675rem] prose-p:text-white prose-p:opacity-80 prose-p:mb-0';
  const label =
    'mb-0 font-geist font-medium text-[2.75rem] leading-[1.4] tracking-[-0.0825rem] text-white';

  return (
    <section
      id="chapter-5"
      data-chapter
      className={cn(
        'relative flex justify-center w-full bg-[#001B14] max-lg:px-[2rem] lg:px-0 max-lg:pt-[8rem] lg:pt-[12.5rem] max-lg:pb-[8rem] lg:pb-[12.5rem]',
        className,
      )}
    >
      <DarkHalftone />

      <div className="relative w-[min(94.5rem,100%)] rounded-[1rem] border border-white/10 bg-white/5 backdrop-blur-[0.65rem] max-lg:px-[2rem] lg:px-[5.25rem] pt-[8.75rem] pb-[4rem]">
        <div className="absolute left-[4rem] top-[-6rem] w-[12rem] h-[11.75rem]">
          <Image
            src={coin}
            alt=""
            className="!w-full !h-full object-contain"
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div className="flex flex-col gap-[3.375rem] w-full">
          {(data?.whyTitle || data?.whyIntro) && (
            <div className="flex flex-col gap-[1.625rem] w-full">
              {data?.whyTitle && (
                <div
                  className="richtext richtext-start w-full lg:w-[61.5rem]
                  prose-p:font-geist prose-p:font-semibold max-lg:prose-p:text-[3.5rem] lg:prose-p:text-[4rem] prose-p:leading-[1.21] max-lg:prose-p:tracking-[-0.105rem] lg:prose-p:tracking-[-0.12rem] prose-p:text-white prose-p:mb-0"
                >
                  <PortableText
                    value={data.whyTitle}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data?.whyIntro && (
                <div className={bodyText}>
                  <PortableText
                    value={data.whyIntro}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          )}

          {(data?.whyNeedLabel || data?.whyNeedItems?.length) && (
            <div className="flex flex-col gap-[1.5rem] w-full">
              {data?.whyNeedLabel && <p className={label}>{data.whyNeedLabel}</p>}
              <CheckList items={data?.whyNeedItems} tone="dark" />
            </div>
          )}

          {(data?.whyGetLabel || data?.whyGetItems?.length) && (
            <div className="flex flex-col gap-[1.75rem] w-full">
              {data?.whyGetLabel && <p className={label}>{data.whyGetLabel}</p>}
              <CheckList items={data?.whyGetItems} tone="dark" />
            </div>
          )}

          {(data?.whyClosingTitle || data?.whyClosingText) && (
            <div className="flex flex-col gap-[2.125rem] w-full">
              {data?.whyClosingTitle && (
                <p className="mb-0 font-geist font-medium text-[3.5rem] leading-[1.21] tracking-[-0.105rem] text-white lg:w-[58.375rem]">
                  {data.whyClosingTitle}
                </p>
              )}
              {data?.whyClosingText && (
                <div
                  className={cn(
                    bodyText,
                    'prose-p:mb-[2.125rem] lg:prose-p:max-w-[48.875rem] lg:[&_p:last-child]:max-w-none',
                  )}
                >
                  <PortableText
                    value={data.whyClosingText}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          )}

          {data?.whyCTA && (
            <CTAButton
              data={data.whyCTA}
              overrideVariant="white"
              className="max-sm:!w-full sm:!w-auto self-start justify-center
              max-sm:[&_.button]:!w-full sm:[&_.button]:!w-[29.75rem] [&_.button]:!min-h-[5.75rem] [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem]
              [&_.button]:!text-[1.75rem] [&_.button]:!tracking-[-0.035rem]
              [&_.button-bg]:!bg-[#FBFBFB]"
            />
          )}
        </div>
      </div>
    </section>
  );
}
